<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { Checkbox, Popup, Search } from "vant";
import { useMoment } from "@/stores/moment";
import { useMate } from "@/stores/mate";
import { Mate, Moment } from "@/types/entity";
import ThreadItem from "@/components/mobile/ThreadItem.vue";
import ScrollLoadingList, {
  ScrollLoadingListInstance,
} from "@/components/mobile/ScrollLoadingList.vue";
import TimeIcon from "@/components/icon/TimeIcon.vue";
import BoxedTab from "@/components/mobile/common/BoxedTab.vue";
import i18n from "@/locales";
import ThreadList from "../ThreadList.vue";
// Props & Emit
const props = withDefaults(
  defineProps<{
    selectedMoments: Record<number, Moment>;
    maxCount?: number;
    collectionId?: number;
    mateId?: number;
  }>(),
  {
    selectedMoments: () => ({}),
  }
);

const emit = defineEmits<{
  (e: "update:selectedMoments", moments: Record<number, Moment>): void;
}>();

const SORTER_ENUM: { text: string; value: "desc" | "asc" }[] = [
  { text: i18n.global.t("story.new"), value: "desc" },
  { text: i18n.global.t("story.old"), value: "asc" },
];

// State
const momentStore = useMoment();
const mateStore = useMate();
const searchInput = ref("");
const matchedText = ref("");
const momentList = ref<Moment[]>([]);
const scrollableEl = ref<HTMLElement | null>(null);
const filteredMomentList = computed(() => {
  // 1. 将数组分为多个数组
  const arrSplitByRootId = momentList.value.reduce((acc, cur) => {
    if (acc.length === 0) {
      acc.push([cur]);
      return acc;
    }
    if (cur.childId !== null) {
      acc[acc.length - 1].push(cur);
    } else {
      acc.push([cur]);
    }
    return acc;
  }, [] as Moment[][]);
  // 2. 对每个数组进行排序
  arrSplitByRootId.forEach((arr) => {
    arr.sort((a, b) =>
      a.rootId || b.rootId
        ? new Date(a.dtCreate!).getTime() - new Date(b.dtCreate!).getTime()
        : new Date(b.dtCreate!).getTime() - new Date(a.dtCreate!).getTime()
    );
  });
  // 3. 合并数组
  return arrSplitByRootId.flat().filter((item) => item.text);
});
const selectedPopupShow = ref(false);
const selectedMomentIds = computed(() => {
  return new Set<number>(Object.keys(props.selectedMoments).map(Number));
});
const selectedMomentList = computed(() => {
  return Object.values(props.selectedMoments);
});
const mate = computed<Mate | null>(() => {
  return momentList.value.length > 0 ? momentList.value[0].mate : null;
});
const selectedSorterIdx = ref(0);
const listRef = ref<ScrollLoadingListInstance | null>(null);
//
watch(selectedSorterIdx, () => {
  momentList.value = [];
  listRef.value?.resetScrollLoadingState();
});
// EventHandler
const scrollLoad = async () => {
  let cursor = undefined;
  if (momentList.value.length > 0) {
    cursor = momentStore.useCursorEncodeByCreate(
      momentList.value[momentList.value.length - 1].dtCreate!,
      true
    );
  }

  const moments = await momentStore.fetchMomentByMateId(
    mateStore.userMate!.id,
    cursor,
    searchInput.value,
    props.collectionId,
    {
      field: "dtCreate",
      order: SORTER_ENUM[selectedSorterIdx.value].value,
    }
  );
  if (moments.length === 0) return false;

  momentList.value.push(...moments);
  return true;
};
const onMomentSelect = (moment: Moment) => {
  if (props.selectedMoments[moment.id]) {
    const newSelectedMoments = { ...props.selectedMoments };
    delete newSelectedMoments[moment.id];
    emit("update:selectedMoments", newSelectedMoments);
  } else if (!props.maxCount || selectedMomentIds.value.size < props.maxCount) {
    emit("update:selectedMoments", {
      ...props.selectedMoments,
      [moment.id]: moment,
    });
  }
};
const onSearch = (val: string) => {
  matchedText.value = val;
  momentList.value = [];
  listRef.value?.resetScrollLoadingState();
};
const resetSearch = () => {
  matchedText.value = "";
  momentList.value = [];
  listRef.value?.resetScrollLoadingState();
};
const randomSelect = (randomCount = 5) => {
  if (randomCount > momentList.value.length) {
    randomCount = momentList.value.length;
  }
  if (props.maxCount && randomCount > props.maxCount) {
    randomCount = props.maxCount;
  }
  const allPossibleIds = momentList.value.map((item) => item.id);
  const randomMomentIds = new Set<number>();
  for (let i = 0; i < randomCount; i++) {
    const idx = Math.floor(Math.random() * allPossibleIds.length);
    randomMomentIds.add(allPossibleIds[idx]);
    allPossibleIds.splice(idx, 1);
  }
  emit(
    "update:selectedMoments",
    momentList.value
      .filter((item) => randomMomentIds.has(item.id))
      .reduce((acc, cur) => {
        acc[cur.id] = cur;
        return acc;
      }, {} as Record<number, Moment>)
  );
};

const reset = async () => {
  emit("update:selectedMoments", {});
  resetSearch();
};

export interface MomentSelectionInstance {
  randomSelect: (randomCount?: number) => void;
  reset: () => Promise<void>;
}

defineExpose<MomentSelectionInstance>({
  randomSelect,
  reset,
});
</script>
<template>
  <div class="w-full overflow-hidden flex flex-col relative h-full">
    <div class="flex gap-1 items-center bg-base-200 pr-2">
      <search
        v-model="searchInput"
        class="flex-grow"
        :placeholder="$t('Message.keywordEnterSerach')"
        @clear="resetSearch"
        @search="onSearch"
      />
      <boxed-tab
        v-model:selected-idx="selectedSorterIdx"
        :disable="listRef?.scrollLoading"
      >
        <div v-for="(item, idx) in SORTER_ENUM" :key="idx">
          {{ item.text }}
        </div>
      </boxed-tab>
    </div>
    <div ref="scrollableEl" class="overflow-y-scroll w-full h-full">
      <ThreadList ref="listRef" :on-scroll-load="scrollLoad" class="min-h-full">
        <div>
          <div
            v-for="item in filteredMomentList"
            :key="item.uuid"
            :class="{
              'bg-base-300': selectedMomentIds.has(item.id),
            }"
            class="flex gap-2 border-b px-4"
            @click="onMomentSelect(item)"
          >
            <div class="h-10 leading-10 pt-4">
              <Checkbox :model-value="selectedMomentIds.has(item.id)" />
            </div>
            <div class="relative flex-grow">
              <thread-item
                :avatar-url="item.mate.mediaUrl"
                :chat-message-list="item.chatMessagezz"
                :content="item.text"
                :enable-audio-play="false"
                :enable-chat-message-click="false"
                :enable-reaction-btn="false"
                :enableTextCollapsed="false"
                :from-mate="item.fromMate"
                :has-next="item.childId != null"
                :has-prev="item.parentId != null"
                :impression-reactions="[]"
                :matched-text="matchedText"
                :mate-id="item.mate.uuid"
                :mate-tone-id="item.mate.mateToneId"
                :media-list="item.mediazz"
                :mention-matezz="item.mentionMatezz ?? []"
                :moment="item"
                :show-avatar="true"
                :show-comment="false"
                :show-reaction="false"
                :time="item.dtCreate"
                :title="item.mate.name"
                :type="item.mate.type"
                :uuid="item.uuid"
                :visibility="item.visibility"
                class="border-b-0"
              />
              <!-- 遮罩层 -->
              <div class="absolute top-0 right-0 left-0 bottom-0"></div>
            </div>
          </div>
        </div>
      </ThreadList>
    </div>
    <transition v-if="false" name="fade">
      <div
        v-show="selectedMomentIds.size > 0 && !selectedPopupShow"
        class="absolute bottom-10 bg-primary text-primary-content py-2 px-4 rounded-2xl flex items-center justify-center gap-2 left-1/2 transform -translate-x-1/2 shadow-2xl"
        @click="selectedPopupShow = true"
      >
        <time-icon class="icon-base" />
        {{ $t("Buttons.selected") }}
        <span class="font-bold"
          >{{ selectedMomentIds.size
          }}{{ props.maxCount ? ` / ${props.maxCount}` : "" }}</span
        >
        {{ $t("Moment.items") }}
      </div>
    </transition>
    <popup
      v-if="false"
      v-model:show="selectedPopupShow"
      class="flex flex-col gap-4 max-h-[80%] min-h-[200px] overflow-hidden"
      position="bottom"
      round
    >
      <div class="w-full h-full grid grid-rows-[auto_1fr] overflow-hidden">
        <div class="navbar bg-base-100 h-top-nav border-b">
          <!-- 左 -->
          <div class="navbar-start h-full"></div>
          <!-- 中 -->
          <div class="navbar-center">
            <div class="font-bold">
              {{ $t("Moment.selected") }} {{ selectedMomentIds.size }}
              {{ $t("Moment.countMoments") }}
            </div>
          </div>
          <!-- 右 -->
          <div class="navbar-end">
            <button
              class="btn btn-sm rounded"
              @click="
                emit('update:selectedMoments', {});
                selectedPopupShow = false;
              "
            >
              {{ $t("Buttons.clearSelection") }}
            </button>
          </div>
        </div>
        <div
          v-if="selectedMomentList.length === 0"
          class="flex flex-col items-center justify-center h-[200px]"
        >
          <div class="flex flex-col items-center justify-center">
            <div class="text-2xl font-bold">
              {{ $t("Moment.noSelectedMoment") }}
            </div>
          </div>
        </div>
        <div v-else class="h-full overflow-y-scroll">
          <div>
            <div
              v-for="item in selectedMomentList"
              :key="item.uuid"
              class="flex gap-2 border-b px-4"
              @click="onMomentSelect(item)"
            >
              <div class="h-10 leading-10 pt-4">
                <Checkbox :model-value="selectedMomentIds.has(item.id)" />
              </div>
              <div class="relative flex-grow">
                <thread-item
                  :avatar-url="mate!.mediaUrl"
                  :chat-message-list="item.chatMessagezz"
                  :content="item.text"
                  :enable-audio-play="false"
                  :enable-chat-message-click="false"
                  :enable-reaction-btn="false"
                  :enableTextCollapsed="false"
                  :from-mate="item.fromMate"
                  :impression-reactions="[]"
                  :mate-id="mate!.uuid"
                  :mate-tone-id="mate!.mateToneId"
                  :media-list="item.mediazz"
                  :mention-matezz="item.mentionMatezz ?? []"
                  :moment="item"
                  :show-avatar="false"
                  :show-comment="false"
                  :show-reaction="false"
                  :time="item.dtCreate"
                  :title="mate!.name"
                  :type="mate!.type"
                  :uuid="item.uuid"
                  :visibility="item.visibility"
                  class="border-b-0"
                />
                <!-- 遮罩层 -->
                <div class="absolute top-0 right-0 left-0 bottom-0"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </popup>
  </div>
</template>
